<template>
  <BaseCard
    class="space-y-5"
    style="scroll-margin-top: calc(4rem + var(--sat))"
  >
    <div class="space-y-1">
      <div class="font-bold md:text-lg">
        {{ $t('games.game_data') }}
      </div>
      <div class="text-black">
        {{ $t(games?.length ? 'showcases.choose_game' : 'showcases.add_game') }}
      </div>
    </div>
    <div
      v-if="games?.length"
      class="flex flex-wrap gap-4"
      :class="companyGamesList.processing && 'opacity-50 pointer-events-none'"
    >
      <div
        v-if="companyGamesList.loaded && !allCompanyGamesLoaded"
        class="w-[160px] h-[120px] overflow-hidden rounded-xl border-primary border-2 bg-primary-100 opacity-50 hover:opacity-100 cursor-pointer"
        @click="loadPreviousGames"
      >
        <div
          class="relative w-full h-full flex justify-center items-center text-primary text-center px-2"
        >
          {{ $t('showcases.load_previous_games') }}
        </div>
      </div>
      <div
        v-for="game in games"
        :key="game.id"
        class="w-[160px] h-[120px] overflow-hidden group cursor-pointer"
        :class="[
          selectedGame?.id === game.id
            ? 'pointer-events-none'
            : 'opacity-50 hover:opacity-100',
          !game.icon?.url && 'bg-primary-100',
        ]"
        @click="selectGame(game)"
      >
        <div class="relative w-full h-full flex justify-center items-center">
          <img
            v-if="game.icon?.url"
            loading="lazy"
            :src="game.icon.url"
            class="w-full h-full object-fill object-center rounded-xl"
            :class="selectedGame?.id !== game.id && 'opacity-50'"
          />
          <BaseIcon
            v-else
            name="outline_gamepad"
            size="none"
            class="text-primary h-14 w-14"
          />
          <div
            v-if="selectedGame?.id === game.id"
            class="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white border border-primary flex justify-center items-center"
          >
            <BaseIcon name="outline_check" size="md" class="text-primary" />
          </div>
          <div
            class="absolute inset-0 rounded-xl border-primary border-2"
            :class="game.icon?.url && 'opacity-0 group-hover:opacity-100'"
          />
          <div
            class="absolute bottom-1 left-3 right-3 text-sm font-extrabold text-center truncate"
            :class="game.icon?.url && 'text-white'"
          >
            {{ game.name }}
          </div>
        </div>
      </div>

      <div
        class="w-[160px] h-[120px] overflow-hidden rounded-xl border-primary border-2 bg-primary-100 cursor-pointer"
        :class="
          selectedGame ? 'opacity-50 hover:opacity-100' : 'pointer-events-none'
        "
        @click="selectGame(null)"
      >
        <div
          class="relative w-full h-full flex flex-col justify-center items-center text-primary text-sm"
        >
          <BaseIcon name="outline_plus" size="none" class="h-[55px] w-[55px]" />
          {{ $t('showcases.add_game_button') }}
        </div>
      </div>
    </div>
    <GamesForm
      ref="formComponent"
      :external-processing="
        props.externalProcessing || companyGamesList.processing || isGameLoading
      "
      is-force-gamefinder-featured
      @submit-request="onFormSubmit"
    />
  </BaseCard>
</template>

<script setup lang="ts">
import GamesForm from '~/components/games/form/GamesForm.vue'
import type { GameLite } from '~/models/games'
import { useGames } from '~/stores/games'

const gamesStore = useGames()
const { companyGamesList, allCompanyGamesLoaded } = storeToRefs(gamesStore)

interface Props {
  initialGame: GameLite | null
  externalProcessing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  initialGame: null,
  externalProcessing: false,
})

const emit = defineEmits<{
  (e: 'loading', value: boolean): void
  (e: 'submitRequest', value: void): void
}>()

defineExpose({
  submit,
  validate,
})

const formComponent = ref<InstanceType<typeof GamesForm> | null>(null)

const isLoading = ref(false)
const isGameLoading = ref(false)
const selectedGame = ref<GameLite | null>(null)

watch(isLoading, (value) => {
  emit('loading', value)
})

watch(
  () => companyGamesList.value.hits,
  async (games: GameLite[]) => {
    if (
      selectedGame.value &&
      !games.some((game) => game.id === selectedGame.value?.id)
    )
      await selectGame(null)
  }
)

const games = computed<GameLite[]>(() =>
  [...(companyGamesList.value.hits || [])]?.reverse()
)

onServerPrefetch(async () => {
  await selectGame(props.initialGame)
})

onMounted(async () => {
  await selectGame(props.initialGame)
})

async function selectGame(game: GameLite | null): Promise<void> {
  if (companyGamesList.value.processing || selectedGame.value === game) {
    return
  }

  selectedGame.value = game

  if (game) {
    isGameLoading.value = true
    await gamesStore.fetchGame(game.id)
    isGameLoading.value = false
  } else {
    gamesStore.resetGame()
  }
}

async function loadPreviousGames(): Promise<void> {
  await gamesStore.nextCompanyGames()
}

function onFormSubmit(): void {
  emit('submitRequest')
}

async function submit(): Promise<void> {
  await formComponent.value?.submit()
}

async function validate(): Promise<boolean> {
  return (await formComponent.value?.validate()) || false
}
</script>
